import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users, Minus, Plus, Loader2, Calendar, Clock, Sparkles } from 'lucide-react';
import { usePropertyContext } from '@/context/PropertyContext';
import { api, type ExperienceDetail, type AvailabilityData } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { BookingData } from './BookingFlow';

interface DateTimeStepProps {
  experience: ExperienceDetail;
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
  onNext: () => void;
  onBack?: () => void;
  mode: 'date' | 'time';
}

/**
 * DateTimeStep - Premium date and time selection
 * Features elegant calendar design and card-based time slots
 */
export function DateTimeStep({ 
  experience, 
  bookingData, 
  onUpdate, 
  onNext, 
  onBack,
  mode 
}: DateTimeStepProps) {
  const { propertyKey } = usePropertyContext();
  const [availability, setAvailability] = useState<AvailabilityData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = bookingData.date ? new Date(bookingData.date) : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  // Fetch availability when month changes
  useEffect(() => {
    async function fetchAvailability() {
      if (!propertyKey) return;
      setIsLoading(true);
      try {
        const monthStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}`;
        const data = await api.getAvailability(propertyKey, experience.id, {
          date: `${monthStr}-01`,
          month: monthStr,
        });
        setAvailability(data);
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchAvailability();
  }, [propertyKey, experience.id, currentMonth]);

  // Generate calendar days
  const calendarDays = generateCalendarDays(currentMonth.year, currentMonth.month, availability);

  const selectedDateSlots = availability.find(a => a.date === bookingData.date)?.slots || [];

  const handleDateSelect = (date: string) => {
    onUpdate({ date, time: '', inventoryId: '' });
  };

  const handleTimeSelect = (time: string, inventoryId: string | undefined) => {
    if (!inventoryId) {
      console.error('[DateTimeStep] Missing inventoryId for time slot:', time);
      return;
    }
    onUpdate({ time, inventoryId });
  };

  const handleGuestChange = (type: 'adult' | 'child', delta: number) => {
    if (type === 'adult') {
      const newCount = Math.max(1, Math.min(experience.maxGuests, bookingData.adultCount + delta));
      onUpdate({ adultCount: newCount });
    } else {
      const newCount = Math.max(0, bookingData.childCount + delta);
      onUpdate({ childCount: newCount });
    }
  };

  const canContinue = mode === 'date' 
    ? !!bookingData.date
    : !!(bookingData.date && bookingData.time && bookingData.inventoryId);

  const totalGuests = bookingData.adultCount + bookingData.childCount;

  // Month navigation
  const goToPrevMonth = () => {
    setCurrentMonth(prev => {
      const d = new Date(prev.year, prev.month - 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const d = new Date(prev.year, prev.month + 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  if (mode === 'date') {
    return (
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <h2 className="font-serif text-xl font-medium text-foreground">Select a Date</h2>
          </div>
          <p className="text-sm text-muted-foreground ml-13">
            Choose when you&apos;d like to experience {experience.name}
          </p>
        </div>

        {/* Guest Count - Premium Card */}
        <div className="mb-6 rounded-xl bg-muted/30 border border-border/30 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background shadow-sm">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="font-semibold text-foreground">Number of Guests</span>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium text-foreground">Adults</span>
                <p className="text-xs text-muted-foreground">Ages 13+</p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleGuestChange('adult', -1)}
                  disabled={bookingData.adultCount <= 1}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-xl border-2',
                    'transition-all duration-200',
                    'hover:border-primary hover:text-primary',
                    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border'
                  )}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center text-lg font-semibold tabular-nums">
                  {bookingData.adultCount}
                </span>
                <button
                  onClick={() => handleGuestChange('adult', 1)}
                  disabled={totalGuests >= experience.maxGuests}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-xl border-2',
                    'transition-all duration-200',
                    'hover:border-primary hover:text-primary',
                    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border'
                  )}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Max guests indicator */}
          <div className="mt-4 pt-4 border-t border-border/30">
            <p className="text-xs text-muted-foreground">
              Maximum {experience.maxGuests} guests per booking
            </p>
          </div>
        </div>

        {/* Premium Calendar */}
        <div className="rounded-xl border border-border/50 bg-card shadow-soft overflow-hidden">
          {/* Month Navigation - Elegant header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/30 bg-muted/20">
            <button
              onClick={goToPrevMonth}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors duration-200'
              )}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h3 className="font-serif text-lg font-medium text-foreground">
              {new Date(currentMonth.year, currentMonth.month).toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h3>
            <button
              onClick={goToNextMonth}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors duration-200'
              )}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="p-4">
            {isLoading ? (
              <div className="flex h-72 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Day headers */}
                <div className="mb-2 grid grid-cols-7 text-center">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {d}
                    </div>
                  ))}
                </div>
                
                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, i) => (
                    <button
                      key={i}
                      onClick={() => day.date && day.available && handleDateSelect(day.date)}
                      disabled={!day.date || !day.available}
                      className={cn(
                        'aspect-square rounded-xl p-2 text-sm font-medium',
                        'transition-all duration-200',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                        // Empty cell
                        !day.date && 'invisible',
                        // Unavailable date
                        day.date && !day.available && 'text-muted-foreground/30 cursor-not-allowed',
                        // Available date
                        day.date && day.available && 'hover:bg-primary/10 hover:text-primary',
                        // Selected date - premium highlight
                        day.date === bookingData.date && [
                          'bg-primary text-primary-foreground',
                          'hover:bg-primary hover:text-primary-foreground',
                          'shadow-sm'
                        ],
                        // Today indicator
                        day.isToday && day.date !== bookingData.date && 'ring-1 ring-primary/30'
                      )}
                    >
                      {day.day}
                      {/* Availability indicator dot */}
                      {day.available && day.date !== bookingData.date && (
                        <span className="block mx-auto mt-0.5 h-1 w-1 rounded-full bg-primary/60" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Legend */}
          <div className="px-5 py-3 border-t border-border/30 bg-muted/10">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-primary" />
                <span>Available</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                <span>Unavailable</span>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onNext}
            disabled={!canContinue}
            className="btn-luxury"
          >
            Continue
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Time selection mode
  const availableSlots = selectedDateSlots.filter(s => s.available);
  const formattedDate = new Date(bookingData.date).toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <h2 className="font-serif text-xl font-medium text-foreground">Select a Time</h2>
        </div>
        <p className="text-sm text-muted-foreground ml-13">
          Available times for <span className="font-medium text-foreground">{formattedDate}</span>
        </p>
      </div>

      {/* Time Slots - Premium Cards */}
      {availableSlots.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {availableSlots.map((slot, index) => {
            const isSelected = slot.time === bookingData.time;
            const isBestAvailability = index === 0 && slot.spotsRemaining > 5;
            
            return (
              <button
                key={slot.inventoryId || slot.time}
                onClick={() => handleTimeSelect(slot.time, slot.inventoryId)}
                className={cn(
                  'relative rounded-xl border-2 p-4 text-center',
                  'transition-all duration-300',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  isSelected 
                    ? 'border-primary bg-primary/5 shadow-gold-glow' 
                    : 'border-border/50 hover:border-primary/50 hover:bg-muted/30'
                )}
              >
                {/* Best availability badge */}
                {isBestAvailability && !isSelected && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                    <Sparkles className="h-2.5 w-2.5" />
                    Best
                  </span>
                )}
                
                <div className={cn(
                  'text-lg font-semibold',
                  isSelected ? 'text-primary' : 'text-foreground'
                )}>
                  {formatTime(slot.time)}
                </div>
                <div className={cn(
                  'text-xs mt-1',
                  slot.spotsRemaining <= 3
                    ? 'text-warning font-medium'
                    : 'text-muted-foreground'
                )}>
                  {slot.spotsRemaining <= 3
                    ? `Only ${slot.spotsRemaining} left!`
                    : `${slot.spotsRemaining} spots`
                  }
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/50">
            <Clock className="h-7 w-7 text-muted-foreground/50" />
          </div>
          <p className="font-medium text-foreground mb-1">No available times</p>
          <p className="text-sm text-muted-foreground">
            Please select a different date
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={onBack}
          className="btn-luxury-ghost"
        >
          <ChevronLeft className="h-5 w-5" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          className="btn-luxury"
        >
          Continue
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// Helper functions
interface CalendarDay {
  day: number | null;
  date: string | null;
  available: boolean;
  isToday: boolean;
}

function generateCalendarDays(year: number, month: number, availability: AvailabilityData[]): CalendarDay[] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days: CalendarDay[] = [];

  // Empty cells for days before first of month
  for (let i = 0; i < firstDay; i++) {
    days.push({ day: null, date: null, available: false, isToday: false });
  }

  // Days of the month
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dateObj = new Date(year, month, d);
    const isPast = dateObj < today;
    const isToday = dateObj.getTime() === today.getTime();
    const hasAvailability = availability.some(a => a.date === date && a.slots.some(s => s.available));
    
    days.push({
      day: d,
      date,
      available: !isPast && hasAvailability,
      isToday,
    });
  }

  return days;
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}
